// noinspection JSIgnoredPromiseFromCall

import baseConfig from "@/config/base.json";
import { deepMerge } from "@/utils/deepMerge";
import { Tenant } from "@moroku/money-webapp-components";
import { type MoneyConfig, MoneyEnvironment, MoneyFeatures } from "@/config/constants";

const viteEnv = import.meta.env;

async function load({
  tenant,
  env,
  url,
  featureConfig,
  loadLocal = false,
}: {
  tenant: Tenant;
  env: MoneyEnvironment;
  url: string;
  featureConfig?: string;
  loadLocal?: boolean;
}): Promise<MoneyConfig> {
  const envConfig: { [P in keyof MoneyConfig]?: any } = {};
  try {
    Object.assign(envConfig, baseConfig);

    deepMerge(envConfig, (await import(`@/config/${tenant}-${env}.json`)).default);

    if (featureConfig) {
      const lendingConfig = (await import(`@/config/${tenant}-${env}-${featureConfig}.json`)).default;
      deepMerge(envConfig, lendingConfig);
    }

    if (loadLocal) {
      deepMerge(envConfig, (await import("./local.js")).default);
      envConfig.isLocal = true;
    }

    envConfig.tenant ??= tenant;
    envConfig.env = env;
    envConfig.origin = url;
    return envConfig as MoneyConfig;
  } catch (err) {
    console.log(`error loading "${env}" env config`, err);
    throw err;
  }
}

async function getConfig(): Promise<MoneyConfig> {
  let env: MoneyEnvironment;
  let tenant = Tenant.demo;
  const url = typeof window !== "undefined" ? window.location?.origin : "localhost";
  let loadLocal = false;

  if (url.includes("dev.")) {
    env = MoneyEnvironment.dev;
  } else if (url.includes("staging.")) {
    env = MoneyEnvironment.stg;
  } else {
    env = MoneyEnvironment.prd;
  }

  if (url.includes("lcu-") || url.includes("lcu.com.au")) {
    tenant = Tenant.lcu;
  } else if (url.includes("bnk-") || url.includes("bnk.com.au")) {
    tenant = Tenant.bnk;
  }

  let featureConfig: string | undefined;
  if (url.includes("lending.")) {
    featureConfig = "lending";
  }

  if (url.includes("onboarding.") || url.includes("join.")) {
    featureConfig = "onboarding";
  }

  if (url.includes("localhost") || url.includes("127.0.0.1")) {
    loadLocal = true;
    // Local runs default to the dev environment config (per README) - never prd.
    env = MoneyEnvironment.dev;
    if (viteEnv.VITE_MONEY_TENANT) {
      tenant = viteEnv.VITE_MONEY_TENANT;
    }
    if (viteEnv.VITE_MONEY_ENV) {
      env = viteEnv.VITE_MONEY_ENV;
    }
    if (viteEnv.VITE_MONEY_PRODUCT) {
      featureConfig = viteEnv.VITE_MONEY_PRODUCT;
    }
  }
  return await load({ tenant, env, url, featureConfig, loadLocal });
}

const _config: MoneyConfig = await getConfig();

export const config = Object.freeze(_config);

// @TODO - replace these with configuration settings
export const envIsLocalOrDev = config?.isLocal === true || config?.env === MoneyEnvironment.dev;
export const envIsDemoLocalOrDev = config?.tenant === Tenant.demo && (config?.isLocal === true || config?.env === MoneyEnvironment.dev);
export const envIsDemoPreProd = config?.tenant === Tenant.demo && config?.env !== MoneyEnvironment.prd;

export const featureEnabled = (feature: MoneyFeatures) => config?.enabledFeatures.includes(feature);
export const onlyFeatureEnabled = (feature: MoneyFeatures) =>
  config?.enabledFeatures.length === 1 && config?.enabledFeatures.includes(feature);
export const onlyFeaturesEnabled = (...features: MoneyFeatures[]) =>
  config?.enabledFeatures.length > 0 && config?.enabledFeatures.every(feature => features.includes(feature));
